import { Position, useReactFlow } from 'reactflow';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Paper } from '@mui/material';

import { CallsMenuStageType } from '@bandido/shared/constants';
import { IVRActionType } from '@bandido/shared/types';

import { useGetTeamById } from '@frontend/features/teams/queries';

import { useCallsMenu } from '../../CallsMenuContext';
import CustomHandle from '../Handle';

import {
  containerStyles,
  headerContainerStyles,
  teamIndicatorStyles,
  dialOptionWrapperStyles,
  titleStyles,
  selectedTitleStyles,
  descriptionStyles,
  NODE_MAX_WIDTH,
} from './styles';
import { StageProps } from './types';
import { getStageIcon } from './utils';
import { SelectedStageModal } from '../SelectedStageModal';
import { WELCOME_MESSAGE } from '../../constants';

const Stage = ({ id, data, type, xPos, yPos }: StageProps) => {
  const { t } = useTranslation();
  const reactFlowInstance = useReactFlow();
  const { onSelectStage, selectedStageId, isConnectedStage } = useCallsMenu();
  const { data: team } = useGetTeamById({ id: data.teamId });

  const isConnected = isConnectedStage(id);

  const isSelected = id === selectedStageId && (isConnected || type === CallsMenuStageType.SUB_MENU);

  const handleStepClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!isConnected && !data.isMain) return;

    onSelectStage(id);

    reactFlowInstance.setCenter(xPos + NODE_MAX_WIDTH + 280, yPos, {
      zoom: 1,
      duration: 800,
    });
  };

  return (
    <>
      <Paper
        className={!isSelected ? 'enable-drag-handle' : ''}
        elevation={isSelected ? 4 : 0}
        sx={containerStyles(isSelected)}
        onClick={handleStepClick}
      >
        <Box sx={headerContainerStyles}>
          <Box display="flex" alignItems="center" gap={1}>
            {getStageIcon(type as CallsMenuStageType, data.isMain)}
            <Typography sx={isSelected ? selectedTitleStyles : titleStyles} variant="subtitle2">
              {data.name === WELCOME_MESSAGE ? t(`callsMenu.${WELCOME_MESSAGE}`) : data.name}
            </Typography>
          </Box>
          {(type === CallsMenuStageType.ACTION || (type === CallsMenuStageType.SUB_MENU && !data.isMain)) && (
            <Box sx={dialOptionWrapperStyles}>{data.dialOption}</Box>
          )}
        </Box>

        {data.action === IVRActionType.SEND_TO_QUEUE && team && (
          <Box display="flex" flexDirection="column" gap={0.5}>
            <Typography variant="body2">{t('callsMenu.stage.team')}</Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Box sx={{ ...teamIndicatorStyles, backgroundColor: team.color }} />
              <Typography color="text.secondary" variant="body2">
                {team.name}
              </Typography>
            </Box>
          </Box>
        )}

        {data.text && (
          <Typography sx={descriptionStyles} variant="body2">
            {data.text}
          </Typography>
        )}

        <>
          {((type === CallsMenuStageType.SUB_MENU && !data.isMain) || type === CallsMenuStageType.ACTION) && (
            <CustomHandle style={{ top: -8 }} type="source" position={Position.Top} />
          )}
          {type === CallsMenuStageType.SUB_MENU && (
            <CustomHandle style={{ bottom: -8 }} type="target" position={Position.Bottom} />
          )}
        </>
      </Paper>
      {isSelected && <SelectedStageModal />}
    </>
  );
};

export default Stage;
